import { Alert, Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import * as React from 'react';

import { ContextStore } from '../store/contextStore';
import { HandoffAnswer, describeHandoffFailure, landingPath, postSandboxHandoff, takeHandoffCode } from './sandboxHandoff';

type State = { phase: 'working' } | { phase: 'failed'; message: string };

/** `/auth/sandbox-handoff#code=…` on a sandbox's dashboard (DSEC D3): rendered before the auth gate (App.js), so it
 *  works whether or not this browser holds a session here. The code leaves the address bar first, is exchanged once,
 *  and the session it buys replaces whatever was stored for this dashboard; then the landing page opens. */
const SandboxHandoffPage = ({ exchange = postSandboxHandoff }: { exchange?: (code: string) => Promise<HandoffAnswer> }) => {
    const { finalizeLogin } = React.useContext(ContextStore) as any;
    const [state, setState] = React.useState<State>({ phase: 'working' });
    const started = React.useRef(false);

    React.useEffect(() => {
        if (started.current) { return; }
        started.current = true;
        const code = takeHandoffCode();
        if (!code) {
            setState({ phase: 'failed', message: describeHandoffFailure({ status: 0, body: { code: 'missing_code' } }) });
            return;
        }
        exchange(code)
            .then(async (answer) => {
                if (answer.status === 201 && answer.body && typeof answer.body.token === 'string') {
                    await finalizeLogin(answer.body, landingPath(answer.body.landing));
                    return;
                }
                setState({ phase: 'failed', message: describeHandoffFailure(answer) });
            })
            .catch(() => setState({ phase: 'failed', message: describeHandoffFailure({ status: 0, body: { code: 'unreachable' } }) }));
    }, [exchange, finalizeLogin]);

    return (
        <Container maxWidth="sm" sx={{ mt: 10 }}>
            <Paper variant="outlined" sx={{ p: 4 }} data-testid="sandbox-handoff">
                <Typography variant="h6" gutterBottom>Opening the sandbox</Typography>
                {state.phase === 'working' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Signing you in to the sandbox…</Typography>
                    </Box>
                ) : (
                    <Alert severity="error" data-testid="sandbox-handoff-error">{state.message}</Alert>
                )}
            </Paper>
        </Container>
    );
};

export default SandboxHandoffPage;
